/**
 * What this browser tells the API about itself, for the backend's request log.
 *
 * Pure helpers: every function takes its inputs as parameters (defaulting to `navigator`) so the
 * specs can feed them any user agent. Nothing here reads or writes storage.
 *
 * The values travel as HTTP request headers (see DeviceInfoInterceptor), and a header value that
 * is not ISO-8859-1 makes the browser reject the whole request. So every value built here comes
 * from fixed ASCII names plus digits, never from raw user-agent text.
 */

export type DeviceType = 'desktop' | 'mobile' | 'tablet';

/** The backend accepts `x-browser` up to this length and cuts anything longer. */
const MAX_BROWSER_LENGTH = 100;

/** `x-country` is a country name; cap it so a bad stored value can never bloat the header. */
const MAX_COUNTRY_LENGTH = 100;

/**
 * Checked in order, first match wins: in-app and Chromium-based browsers also carry a `Chrome/`
 * token, and Chrome carries a `Safari/` one, so the more specific rules must come first.
 */
const BROWSER_RULES: ReadonlyArray<readonly [name: string, pattern: RegExp]> = [
  ['MetaMask Mobile', /MetaMaskMobile/i],
  ['Edge', /Edg(?:e|A|iOS)?\/(\d+)/],
  ['Opera', /(?:OPR|OPT)\/(\d+)/],
  ['Samsung Internet', /SamsungBrowser\/(\d+)/],
  ['UC Browser', /UCBrowser\/(\d+)/],
  ['Firefox', /(?:Firefox|FxiOS)\/(\d+)/],
  ['Chrome', /CriOS\/(\d+)/],
  ['Android WebView', /; wv\).*Chrome\/(\d+)/],
  ['Chrome', /Chrome\/(\d+)/],
  ['Safari', /Version\/(\d+)[\d.]*.*Safari\//],
];

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function readUserAgent(): string {
  return typeof navigator === 'undefined' ? '' : navigator.userAgent ?? '';
}

function readPlatform(): string {
  return typeof navigator === 'undefined' ? '' : navigator.platform ?? '';
}

function readMaxTouchPoints(): number {
  return typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints ?? 0;
}

/**
 * Classifies the device as the backend expects it: exactly `desktop`, `mobile` or `tablet`
 * (any other value is stored as null).
 * @param userAgent the browser's user agent
 * @param platform `navigator.platform`, used to spot iPadOS
 * @param maxTouchPoints `navigator.maxTouchPoints`, used to spot iPadOS
 * @returns the device class
 */
export function getDeviceType(
  userAgent: string = readUserAgent(),
  platform: string = readPlatform(),
  maxTouchPoints: number = readMaxTouchPoints(),
): DeviceType {
  // Android tablets are the Android user agents without a "Mobile" token.
  if (/iPad|Tablet|PlayBook|Silk|Kindle|Android(?!.*Mobile)/i.test(userAgent)) return 'tablet';
  // iPadOS 13+ Safari reports a desktop Mac user agent; touch support is the only tell.
  if (platform === 'MacIntel' && maxTouchPoints > 1) return 'tablet';
  if (/Mobi|iPhone|iPod|Android|Windows Phone|IEMobile|BlackBerry|BB10|Opera Mini/i.test(userAgent)) return 'mobile';
  return 'desktop';
}

/**
 * Names the browser and its major version, e.g. `Chrome 128` or `MetaMask Mobile`.
 * @param userAgent the browser's user agent
 * @returns the browser label, `Other` for an unrecognised user agent, or null when there is none
 */
export function getBrowser(userAgent: string = readUserAgent()): string | null {
  if (!userAgent) return null;
  for (const [name, pattern] of BROWSER_RULES) {
    const match = userAgent.match(pattern);
    if (match) {
      const label = match[1] ? `${name} ${match[1]}` : name;
      return label.slice(0, MAX_BROWSER_LENGTH);
    }
  }
  return 'Other';
}

/**
 * Whether this is the Brave browser.
 *
 * Brave deliberately reports Chrome's user agent (anti-fingerprinting), so `getBrowser()` can
 * only ever see `Chrome <n>` for it — the string carries no "Brave" token to match. The one
 * supported signal is the asynchronous, non-standard `navigator.brave.isBrave()` API, which lives
 * outside the user agent and so outside the pure UA helpers above.
 *
 * Fails closed: returns false when the API is absent (every non-Brave browser, and very old
 * Brave) or when it throws, so a caller can safely fall back to the UA-derived label.
 * @param nav the navigator-like object exposing `brave.isBrave` (defaults to `navigator`)
 * @returns a promise resolving to true only for Brave
 */
export async function isBraveBrowser(
  nav: { brave?: { isBrave?: () => Promise<boolean> } } =
    typeof navigator === 'undefined' ? {} : (navigator as unknown as { brave?: { isBrave?: () => Promise<boolean> } }),
): Promise<boolean> {
  try {
    return nav?.brave?.isBrave ? await nav.brave.isBrave() : false;
  } catch {
    return false;
  }
}

/**
 * Whether a value is a device id as the backend issues it (a UUID). The backend stores any other
 * `x-device-id` value as null, so nothing else is kept or sent.
 * @param value the candidate
 * @returns true for a UUID string
 */
export function isDeviceId(value: unknown): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

/**
 * Turns a stored profile country into a header-safe `x-country` value, or null when there is none.
 *
 * The country comes from the user's own profile (available only after login), so it is accurate
 * but free-form text. A header value outside ISO-8859-1 makes the browser reject the whole
 * request, so any character above code point 255 is dropped rather than risk a failed API call;
 * the value is trimmed and capped. `-` is the app's "not set" placeholder and counts as absent.
 * @param value the stored country, usually `userDetails.user.country`
 * @returns a header-safe country, or null when there is nothing worth sending
 */
export function getCountryHeaderValue(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const latin1Only = Array.from(value)
    .filter(char => char.charCodeAt(0) <= 0xff)
    .join('');
  const trimmed = latin1Only.trim();
  if (!trimmed || trimmed === '-') return null;
  return trimmed.slice(0, MAX_COUNTRY_LENGTH).trim();
}
